<template>
    <!-- HEADER FIXE -->
    <header
        class="bg-[#2A4493] text-white px-4 py-3 flex items-center justify-between fixed top-0 left-0 w-full z-50 shadow-md h-16">
        <div class="flex items-center space-x-3">
            <button @click="sidebarVisible = !sidebarVisible" class="text-white lg:hidden">
                <i class="fas fa-bars text-lg"></i>
            </button>
            <div>
                <h1 class="text-sm font-medium">{{ book.metadata.title }}</h1>
                <p class="text-xs opacity-90">{{ book.metadata.subtitle }}</p>
            </div>
        </div>

        <div class="flex items-center gap-3 md:gap-5">
            <div class="hidden md:flex items-center gap-2">
        <span class="bg-white text-[#2A4493] px-3 py-2 rounded-lg shadow hover:bg-gray-100 cursor-pointer text-sm">
          {{ currentPage }} / {{ totalPages }}
        </span>
            </div>
            <div class="flex items-center gap-2">
                <button @click="zoomOut" class="bg-white text-[#2A4493] p-2 rounded-lg shadow hover:bg-gray-100 cursor-pointer">
                    <i class="fas fa-search-minus"></i>
                </button>
                <button @click="zoomIn" class="bg-white text-[#2A4493] p-2 rounded-lg shadow hover:bg-gray-100 cursor-pointer">
                    <i class="fas fa-search-plus"></i>
                </button>
                <button @click="previousPage" class="bg-white text-[#2A4493] p-2 rounded-lg shadow hover:bg-gray-100 cursor-pointer">
                    <i class="fas fa-chevron-up"></i>
                </button>
                <button @click="nextPage" class="bg-white text-[#2A4493] p-2 rounded-lg shadow hover:bg-gray-100 cursor-pointer">
                    <i class="fas fa-chevron-down"></i>
                </button>
            </div>
        </div>

        <div class="hidden md:flex items-center space-x-4">
            <div class="bg-white text-[#2A4493] px-3 py-1 rounded-full text-sm font-mono font-semibold shadow">
                ⏱ {{ formattedTime }}
            </div>
            <button v-if="String(book.metadata?.droits?.telechargeable) === 'on'" @click="download(book.id)"
                    class="bg-white text-[#2A4493] p-2 rounded shadow hover:bg-gray-100 cursor-pointer">
                <i class="fas fa-download"></i> Télécharger
            </button>
            <button @click="quit(book)"
                    class="bg-white text-[#2A4493] p-2 rounded-full shadow hover:bg-gray-100 cursor-pointer">
                <i class="fas fa-times"></i>
            </button>
            <button class="text-white">
                <i class="fas fa-ellipsis-vertical text-lg"></i>
            </button>
        </div>

        <!-- Menu mobile compact -->
        <div class="md:hidden flex items-center space-x-2">
            <div class="bg-white text-[#2A4493] px-2 py-1 rounded-lg text-xs font-mono font-semibold shadow">
                ⏱ {{ formattedTime }}
            </div>
            <button @click="quit(book)"
                    class="bg-white text-[#2A4493] p-2 rounded-full shadow hover:bg-gray-100 cursor-pointer">
                <i class="fas fa-times"></i>
            </button>
        </div>
    </header>

    <!-- OVERLAY MOBILE -->
    <div v-if="isMobile && sidebarVisible" @click="sidebarVisible = false"
         class="fixed inset-0 bg-black bg-opacity-40 z-10 lg:hidden"></div>

    <!-- CORPS DE LA PAGE -->
    <div class="flex pt-16 h-[calc(100vh-64px)]">
        <!-- SIDEBAR -->
        <aside
            :class="[
        'fixed bg-white shadow-xl flex flex-col h-full z-20 transition-all duration-300',
        sidebarVisible && !isMobile ? 'w-[25%]' : 'w-0 overflow-hidden',
        isMobile && sidebarVisible ? 'w-full' : isMobile ? 'w-0' : ''
      ]">
            <!-- Barre de recherche -->
            <div class="p-4 border-b border-gray-200">
                <div class="relative">
                    <input
                        type="text"
                        placeholder="Rechercher dans les chapitres..."
                        v-model="searchQuery"
                        @input="onSearchInput"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A4493]">
                    <i class="fas fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <button v-if="searchQuery" @click="clearSearch"
                            class="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div v-if="searchQuery" class="mt-2 text-xs text-gray-500">
                    {{ filteredChapters.length }} résultat(s)
                </div>
            </div>

            <!-- Scroll chapitres -->
            <div class="flex-1 overflow-y-auto p-4 space-y-2" style="max-height: calc(100vh - 142px);">
                <!-- Résultats de recherche -->
                <div v-if="searchQuery.trim()" class="space-y-2">
                    <div v-for="(item, index) in filteredChapters" :key="index" @click="pagine(item)"
                         class="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition shadow-sm hover:shadow">
                        <div class="flex items-center space-x-3">
                            <div :class="[
                  'w-3 h-3 rounded-full',
                  item.level == 1 ? 'bg-blue-500' : item.level == 2 ? 'bg-emerald-400' : 'bg-orange-400'
                ]"></div>
                            <span v-html="highlightSearchTerm(item.title)" class="text-sm font-medium"></span>
                        </div>
                        <span class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">p. {{ item.pageNumber }}</span>
                    </div>
                </div>

                <!-- Accordéon -->
                <div v-else class="space-y-2">
                    <div v-for="(group, index) in groupedChapters" :key="index">
                        <div @click="toggleAccordion(index)"
                             class="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition shadow-sm">
                            <div class="flex items-center space-x-3">
                                <div class="w-3 h-3 bg-[#2A4493] rounded-full"></div>
                                <span class="text-sm font-semibold text-[#2A4493]">{{ group.chapter.title }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs bg-blue-100 text-[#2A4493] px-2 py-1 rounded">p. {{ group.chapter.pageNumber }}</span>
                                <i :class="expandedChapters[index] ? 'fa-chevron-up' : 'fa-chevron-down'" class="fas text-[#2A4493] text-xs"></i>
                            </div>
                        </div>

                        <div v-if="expandedChapters[index]" class="ml-6 mt-2 space-y-2">
                            <div v-for="item in group.subItems" :key="item.id" @click="pagine(item)"
                                 class="p-3 rounded-lg hover:bg-emerald-50 cursor-pointer transition shadow-sm"
                                 :class="item.level == 3 ? 'ml-6' : ''">
                                <div class="flex items-center space-x-3">
                                    <div :class="[
                      'w-2 h-2 rounded-full',
                      item.level == 2 ? 'bg-emerald-400' : 'bg-orange-400'
                    ]"></div>
                                    <span class="text-sm text-gray-700">{{ item.title }}</span>
                                </div>
                                <span class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded float-right">p. {{ item.pageNumber }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </aside>

        <!-- CONTENU PRINCIPAL -->
        <main
            :class="[
        'bg-gray-100 p-4 overflow-y-auto h-[calc(100vh-80px)] transition-all duration-300',
        sidebarVisible && !isMobile ? 'ml-[25%] w-[75%]' : 'ml-0 w-full'
      ]"
            ref="mainContainer"
            @scroll="onScroll">
            <button @click="scrollToTop"
                    class="fixed bottom-6 right-6 bg-[#2A4493] text-white p-3 rounded-full shadow-lg hover:bg-[#2A4492] transition z-10">
                <i class="fas fa-arrow-up"></i>
            </button>
            <div id="pdf-container" class="w-full"></div>
        </main>
    </div>
</template>
<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef} from 'vue'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import pdfWorker from "pdfjs-dist/build/pdf.worker?url";
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

// // Use the .mjs worker file you found
// pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs'

const props = defineProps({
    book: {
        type: Object,
        required: true,
    },
    user: {
        type: [Object, null],
        default: null,
        required: true,
    },
})

const sessionKey = `reading_session_${props.book?.id}_${props.user?.id}`
const elapsedTime = ref(0)
const startTime = ref(Date.now())
let intervalId = null

function initializeSessionTime() {
    const stored = localStorage.getItem(sessionKey)

    if (stored) {
        startTime.value = parseInt(stored) // ✅ Reprendre la session
    } else {
        startTime.value = Date.now()
        localStorage.setItem(sessionKey, startTime.value.toString())
    }
}
setInterval(() => {
    elapsedTime.value = Math.floor((Date.now() - startTime.value) / 1000)
}, 1000)

const formattedTime = computed(() => {
    const h = Math.floor(elapsedTime.value / 3600).toString().padStart(2, '0')
    const m = Math.floor((elapsedTime.value % 3600) / 60).toString().padStart(2, '0')
    const s = (elapsedTime.value % 60).toString().padStart(2, '0')
    return `${h}:${m}:${s}`
})

onBeforeUnmount(() => {
    clearInterval(intervalId)
})

const urlBase = window.location.origin


const quit = (book) => {
    $.confirm({
        icon: 'fa fa-question',
        title: 'Confirmation',
        content: 'Êtez-vous sûr de pouvoir quitter la page',
        useBootstrap: false,
        boxWidth: '500px',
        // autoClose: 'Annuler|10000',
        theme: 'modern',
        closeIcon: true,
        animation: 'scale',
        type: 'red',
        buttons: {
            btn: {
                text: 'Confirmer',
                btnClass: 'btn-red',
                action: function () {
                    sendReadingTime()
                    nextTick(async () => {
                        window.location.href = urlBase + `/show/${book.id}`
                    })
                }
            },
            Annuler: function () {
            }
        }
    });


}

function sendReadingTime() {
    if (!props.book?.id || !props.user?.id) return
    const duration = Math.floor((Date.now() - startTime.value) / 1000)

    const payload = JSON.stringify({
        book_id: props.book?.id,
        user_id: props.user?.id,
        time_spent: duration
    })

    const blob = new Blob([payload], {type: 'application/json'})
    const success = navigator.sendBeacon('/api/lecture/time', blob)

    if (success) {
        localStorage.removeItem(sessionKey)
    }
}

// Variables pour la recherche
const searchQuery = ref('')
const searchResults = ref([])

// État de l'accordéon
const expandedChapters = ref({})

// Fonction de recherche avec debounce
let searchTimeout = null

const onSearchInput = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout)
    }

    searchTimeout = setTimeout(() => {
        performSearch()
    }, 300) // Délai de 300ms pour éviter trop d'appels
}

// Fonction pour effectuer la recherche
const performSearch = () => {
    if (!searchQuery.value.trim()) {
        searchResults.value = []
        return
    }

    const query = searchQuery.value.toLowerCase().trim()
    const allChapters = props.book.metadata.chapitres || []

    searchResults.value = allChapters.filter(chapter => {
        return chapter.title.toLowerCase().includes(query)
    })
}

// Fonction pour effacer la recherche
const clearSearch = () => {
    searchQuery.value = ''
    searchResults.value = []
}

// Computed pour les chapitres filtrés (recherche ou tous)
const filteredChapters = computed(() => {
    if (!searchQuery.value.trim()) {
        return props.book.metadata.chapitres || []
    }
    return searchResults.value
})

// Fonction pour surligner les termes de recherche
const highlightSearchTerm = (text) => {
    if (!searchQuery.value.trim()) {
        return text
    }

    const regex = new RegExp(`(${searchQuery.value})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-200 text-yellow-800 px-1 rounded">$1</mark>')
}

// Fonction pour grouper les chapitres (mode normal, sans recherche)
const groupedChapters = computed(() => {
    if (!props.book.metadata.chapitres || props.book.metadata.chapitres.length === 0) {
        return []
    }

    const groups = []
    let currentGroup = null

    props.book.metadata.chapitres.forEach((item, index) => {
        if (parseInt(item.level) === 1) {
            // Nouveau groupe pour niveau 1
            if (currentGroup) {
                groups.push(currentGroup)
            }
            currentGroup = {
                chapter: item,
                subItems: []
            }
        } else if (currentGroup && (parseInt(item.level) === 2 || parseInt(item.level) === 3)) {
            // Ajouter aux sous-éléments du groupe actuel
            currentGroup.subItems.push(item)
        }
    })

    // Ajouter le dernier groupe
    if (currentGroup) {
        groups.push(currentGroup)
    }

    return groups
})

// Fonction pour toggle l'accordéon
const toggleAccordion = (index) => {
    expandedChapters.value[index] = !expandedChapters.value[index]
}


const download = async (bookId) => {
    try {
        const response = await axios.get(`/api/documents/${bookId}/download`, {
            responseType: 'blob' // très important pour les fichiers
        });

        const blob = new Blob([response.data], {type: 'application/pdf'});

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${props.book?.metadata.title}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.log(error)
        console.error('Erreur de téléchargement', error);
    }
}


// Variables pour le PDF
// const sidebarVisible = ref(true)
const pdf = shallowRef()
const currentPage = ref(1)
const totalPages = ref(0)
const currentScale = ref(1.7)
const mainContainer = ref(null)
const isScrolling = ref(false)

function getCurrentVisiblePage() {
    if (!mainContainer.value || !pdf.value) return currentPage.value

    const container = mainContainer.value
    const containerTop = container.scrollTop
    const containerHeight = container.clientHeight
    const containerCenter = containerTop + containerHeight / 2

    for (let i = 1; i <= pdf.value.numPages; i++) {
        const pageElement = document.getElementById(`page-${i}`)
        if (pageElement) {
            const pageTop = pageElement.offsetTop - container.offsetTop
            const pageBottom = pageTop + pageElement.offsetHeight

            if (containerCenter >= pageTop && containerCenter <= pageBottom) {
                return i
            }
        }
    }

    return currentPage.value
}

const isMobile = ref(window.innerWidth < 768)
const sidebarVisible = ref(!isMobile.value)

const handleResize = () => {
    isMobile.value = window.innerWidth < 768
    if (!isMobile.value) sidebarVisible.value = true
}

onMounted(() => {
    window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
})

// Gestionnaire de scroll avec throttle
let scrollTimeout = null

function onScroll() {
    isScrolling.value = true

    if (scrollTimeout) {
        clearTimeout(scrollTimeout)
    }

    scrollTimeout = setTimeout(() => {
        const visiblePage = getCurrentVisiblePage()
        if (visiblePage !== currentPage.value) {
            currentPage.value = visiblePage
        }
        isScrolling.value = false
    }, 100)
}

// Fonctions PDF
// Dans votre fonction renderAllPages(), remplacez-la par cette version :

async function renderAllPages() {
    const container = document.getElementById('pdf-container')
    container.innerHTML = ''

    for (let pageNumber = 1; pageNumber <= pdf.value.numPages; pageNumber++) {
        const page = await pdf.value.getPage(pageNumber)
        const viewport = page.getViewport({scale: currentScale.value})

        // Créer le wrapper de la page
        const pageWrapper = document.createElement('div')
        pageWrapper.id = `page-${pageNumber}`
        pageWrapper.style.margin = '20px auto'
        pageWrapper.style.padding = '10px'
        pageWrapper.style.background = '#fff'
        pageWrapper.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.1)'
        pageWrapper.style.border = '1px solid #ddd'
        pageWrapper.style.width = `${viewport.width}px`
        pageWrapper.style.maxWidth = '100%'
        pageWrapper.style.borderRadius = '6px'
        pageWrapper.style.overflowX = 'auto'
        pageWrapper.style.textAlign = 'center'
        pageWrapper.style.position = 'relative' // Important pour le positionnement de la text layer

        // Créer le canvas
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.display = 'block'
        canvas.style.maxWidth = '100%'
        const context = canvas.getContext('2d')

        // Créer le conteneur pour la text layer
        const textLayerDiv = document.createElement('div')
        textLayerDiv.className = 'textLayer'
        textLayerDiv.style.position = 'absolute'
        textLayerDiv.style.left = '0'
        textLayerDiv.style.top = '0'
        textLayerDiv.style.right = '0'
        textLayerDiv.style.bottom = '0'
        textLayerDiv.style.overflow = 'hidden'
        textLayerDiv.style.opacity = '0.2' // Légèrement visible pour debug (optionnel)
        textLayerDiv.style.lineHeight = '1.0'

        // Ajouter le canvas et la text layer au wrapper
        pageWrapper.appendChild(canvas)
        pageWrapper.appendChild(textLayerDiv)
        container.appendChild(pageWrapper)

        // Rendre le canvas
        await page.render({canvasContext: context, viewport}).promise

        // Rendre la text layer - Approche alternative
        try {
            const textContent = await page.getTextContent()

            // Créer manuellement les éléments de texte
            const textItems = textContent.items

            textItems.forEach((item, index) => {
                if (item.str.trim() === '') return // Ignorer les éléments vides

                const textDiv = document.createElement('span')
                textDiv.style.position = 'absolute'
                textDiv.style.left = `${item.transform[4]}px`
                textDiv.style.top = `${viewport.height - item.transform[5] - item.height}px`
                textDiv.style.fontSize = `${item.height}px`
                textDiv.style.fontFamily = item.fontName || 'sans-serif'
                textDiv.style.color = 'transparent'
                textDiv.style.cursor = 'text'
                textDiv.style.userSelect = 'text'
                textDiv.textContent = item.str

                // Transformation pour l'alignement
                const transform = `scale(${item.transform[0]}, ${item.transform[3]})`
                textDiv.style.transform = transform
                textDiv.style.transformOrigin = '0 0'

                textLayerDiv.appendChild(textDiv)
            })
        } catch (textError) {
            console.warn('Erreur lors du rendu de la text layer:', textError)
        }
    }

    await nextTick()
    currentPage.value = getCurrentVisiblePage()
}

const zoomIn = () => {
    currentScale.value = Math.min(currentScale.value + 0.2, 4)
    renderAllPages()
}

const zoomOut = () => {
    currentScale.value = Math.max(currentScale.value - 0.2, 0.5)
    renderAllPages()
}

const nextPage = () => {
    const visiblePage = getCurrentVisiblePage()
    const nextPageNum = Math.min(visiblePage + 1, totalPages.value)

    if (nextPageNum !== visiblePage) {
        currentPage.value = nextPageNum
        scrollToPage(nextPageNum)
    }
}

const previousPage = () => {
    const visiblePage = getCurrentVisiblePage()
    const prevPageNum = Math.max(visiblePage - 1, 1)

    if (prevPageNum !== visiblePage) {
        currentPage.value = prevPageNum
        scrollToPage(prevPageNum)
    }
}

const scrollToPage = (pageNumber) => {
    const el = document.getElementById(`page-${pageNumber}`)
    if (el && mainContainer.value) {
        const containerTop = mainContainer.value.offsetTop
        const elementTop = el.offsetTop - containerTop

        mainContainer.value.scrollTo({
            top: elementTop - 20,
            behavior: 'smooth'
        })
    }
}

const scrollToTop = () => {
    if (mainContainer.value) {
        mainContainer.value.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
        currentPage.value = 1
    }
}
const pagine = (item) => {
    const pageNum = parseInt(item.pageNumber)
    currentPage.value = pageNum
    scrollToPage(pageNum)
    if (isMobile.value) {
        sidebarVisible.value = false
    }
}

// Initialisation au montage
onMounted(async () => {
    // Charger le PDF
    initializeSessionTime()

    const url = `http://localhost:8000/api/documents/${props.book.id}/stream`

    try {
        const response = await fetch(url)

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Server returned ${response.status}: ${response.statusText}`)
        }

        const contentType = response.headers.get('Content-Type')
        if (!contentType || !contentType.includes('application/pdf')) {
            console.warn('Response is not a PDF:', contentType)
        }

        const loadingTask = pdfjsLib.getDocument(url)
        pdf.value = await loadingTask.promise
        totalPages.value = pdf.value.numPages

        await renderAllPages()
    } catch (err) {
        console.error('Erreur PDF.js :', err)

        const container = document.getElementById('pdf-container')
        container.innerHTML = `
            <div style="padding: 20px; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 5px;">
                <h4>Erreur de chargement du PDF</h4>
                <p><strong>Document ID:</strong> ${props.book.id}</p>
                <p><strong>URL:</strong> ${url}</p>
                <p><strong>Erreur:</strong> ${err.message}</p>
            </div>
        `
    }
})

</script>
<style scoped>
.textLayer {
    position: absolute;
    text-align: initial;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    line-height: 1.0;
}

.textLayer span,
.textLayer br {
    color: transparent;
    position: absolute;
    white-space: pre;
    cursor: text;
    transform-origin: 0% 0%;
}

.textLayer span::selection {
    background: rgba(0, 0, 255, 0.3);
}

.textLayer br::selection {
    background: rgba(0, 0, 255, 0.3);
}
</style>
